import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import styled from 'styled-components';
import AransciaLogo from '../assets/images/aranscia-logo.svg';
import AransciaLogoDark from '../assets/images/aranscia-2024-logo.png';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: var(--background-color);
  padding: var(--spacing-md);

  @media (min-width: 600px) {
    min-height: 80vh;
  }
`;

const LoginCard = styled.div`
  background-color: var(--background-light);
  border-radius: var(--border-radius);
  padding: var(--spacing-xl);
  box-shadow: 0 4px 16px rgba(23, 39, 79, 0.10);
  max-width: 400px;
  width: 100%;
  text-align: center;
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--secondary-color);
  border-radius: 16px;
  width: 140px;
  height: 48px;
  margin: 0 auto var(--spacing-xl) auto;
  box-shadow: 0 2px 8px rgba(23, 39, 79, 0.12);
`;

const Logo = styled.img`
  width: 120px;
  height: 32px;
  object-fit: contain;
`;

const Tagline = styled.p`
  color: var(--secondary-color);
  font-weight: 500;
  margin-bottom: 1rem;
  margin-top: -0.5rem;
`;

const LoginButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--border-radius);
  font-size: 1rem;
  font-weight: 600;
  width: 100%;
  margin-top: var(--spacing-lg);
  transition: background-color var(--transition-speed) ease;
  opacity: ${props => props.disabled ? 0.7 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  &:hover {
    background-color: ${props => props.disabled ? 'var(--primary-color)' : 'var(--primary-dark)'};
  }

  &:focus {
    outline: 2px solid var(--primary-light);
    outline-offset: 2px;
  }
`;

const LoginPage = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDarkTheme, setIsDarkTheme] = React.useState(false);

  React.useEffect(() => {
    // Check if dark theme is enabled
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    setIsDarkTheme(isDark);
    
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleLogin = async () => {
    try {
      await login();
      // No need to do anything here - redirect will happen automatically
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LogoWrapper>
          <Logo src={isDarkTheme ? AransciaLogoDark : AransciaLogo} alt="Aranscia logo" role="img" />
        </LogoWrapper>
        <Tagline>Empowering human-first diagnostic solutions</Tagline>
        <h1>Welcome to Aranscia PHI Training</h1>
       
        <LoginButton
          onClick={handleLogin}
          disabled={isLoading}
          aria-label="Sign in with Microsoft"
        >
          {isLoading ? 'Signing in...' : 'Sign in'}
        </LoginButton>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage; 